/**
 * @file bug.tsx
 * @description Компонент страницы каталога багов для управления сущностями ошибок в системе управления задачами. 
 * Предоставляет комплексную функциональность отслеживания багов включая создание, редактирование, фильтрацию и 
 * просмотр с поддержкой уровней приоритета, управления статусами, классификации контуров и связей с задачами. 
 * Включает многовкладочный интерфейс с основным обзором, связанными задачами и отслеживанием истории. 
 * Поддерживает расширенную фильтрацию по сервису, статусу, приоритету, контуру и ответственному лицу.
 * @author AI Assistant
 * @created 2025-09-04
 * @updated 2025-09-04
 * @dependencies ['@forms/bug', '@utils/table-view', '@features/filter/types', '@forms/bug/tabs', 'react-dom/server', '@src/components/icons/priority-cell-header']
 * @related-files ['@forms/bug/index.tsx', '@forms/bug/tabs/Main.tsx', '@forms/bug/tabs/History.tsx', '@forms/bug/tabs/TaskTab.tsx']
 */

import { BugForm } from '@forms/bug';
import { getBadgeCell, getContourCell, getDateTimeCell, getDefaultCell, getPriorityCell } from '@utils/table-view';
import { tabObject, TBaseObjectDto } from '@src/types';
import { filterComponent } from '@features/filter/types';
import { Main, History, TaskTab } from '@forms/bug/tabs';
import { renderToStaticMarkup } from 'react-dom/server';
import { PriorityCellHeader } from '@src/components/icons/priority-cell-header';

export const title = 'Ошибки';
export const searchModalTitle = 'Выбор ошибки';
export const type = 'Bug';
export const path = 'bug';
export const form = BugForm;
export const isCanAddGroups = true;
export const tabs: tabObject[] = [
    { label: 'Обзор', component: Main },
    { label: 'Задача', component: TaskTab, tab: 'task' },
    { label: 'История', component: History, tab: 'histories' }
];

export const columns = [
    {
        id: '$item',
        header: [{ text: 'Код' }],
        width: 140,
        render: (cellValue: any) => getDefaultCell(cellValue)
    },
    {
        id: 'date',
        header: [{ text: 'Дата' }],
        width: 130,
        render: (cellValue: number) => getDateTimeCell(cellValue)
    },
    {
        id: 'state',
        header: [{ text: 'Статус' }],
        width: 115,
        render: (cellValue: any) => getBadgeCell(cellValue)
    },
    {
        id: 'priority',
        header: [{ text: renderToStaticMarkup(<PriorityCellHeader />), htmlEnable: true }],
        width: 32,
        align: 'center',
        render: (cellValue: any) => getPriorityCell(cellValue)
    },
    {
        id: 'name',
        header: [{ text: 'Наименование' }],
        render: (cellValue: any) => getDefaultCell(cellValue)
    },
    {
        id: 'contour',
        header: [{ text: 'Контур' }],
        width: 84,
        align: 'center',
        render: (cellValue: any) => getContourCell(cellValue)
    },
    {
        id: 'service',
        header: [{ text: 'Сервис' }],
        width: 230,
        render: (cellValue: any) => getDefaultCell(cellValue)
    },
    {
        id: 'executor',
        header: [{ text: 'Ответственный' }],
        width: 190,
        render: (cellValue: any) => getDefaultCell(cellValue)
    }
];

export const getFilterControls = (data: TBaseObjectDto = {}): filterComponent[] => [
    {
        filterType: 'dictionary',
        filterProps: {
            label: 'Сервис',
            type: 'Service'
        },
        value: data.service,
        name: 'service',
        tab: 'filters'
    },
    {
        filterType: 'select',
        filterProps: {
            label: 'Статус',
            emptyItem: 'Все'
        },
        value: data.state,
        type: 'ETaskState',
        name: 'state',
        tab: 'filters'
    },
    {
        filterType: 'select',
        filterProps: {
            label: 'Приоритет',
            emptyItem: 'Все'
        },
        value: data.priority,
        type: 'EBugPriority',
        name: 'priority',
        tab: 'filters'
    },
    {
        filterType: 'select',
        filterProps: {
            label: 'Контур',
            emptyItem: 'Все'
        },
        value: data.contour,
        type: 'EDevelopmentContour',
        name: 'contour',
        tab: 'filters'
    },
    {
        filterType: 'dictionary',
        filterProps: {
            label: 'Ответственный',
            type: 'User'
        },
        value: data.executor,
        name: 'executor',
        tab: 'filters'
    }
];
